import { getTranslation } from '@easy-expense/intl-client';
import type { CheckoutEventsData } from '@paddle/paddle-js';

export type BillingInterval = 'day' | 'week' | 'month' | 'year';

type TimePeriod = { frequency: number; interval: BillingInterval };

/**
 * What a checkout's order summary shows. Paddle's inline checkout renders no totals of its own,
 * so this is the buyer's only view of what they will pay, and every field is fed from Paddle's
 * own checkout events rather than from a list price.
 */
export type CheckoutSummary = {
  currencyCode: string;
  /** Paddle line-item quantity. Seats on the Team plan; always 1 on an individual plan. */
  quantity: number;
  /** Recurring price of one unit before tax. */
  unitPrice: number;
  /** Recurring charge before tax, all units. */
  subtotal: number;
  /** Recurring discount taken off the subtotal; zero unless the buyer applied a code. */
  discount: number;
  /** Recurring tax; zero until Paddle knows the buyer's address. */
  tax: number;
  /** What the card is charged right now (zero while a trial applies). */
  dueToday: number;
  /** What each renewal charges, tax included. */
  recurringTotal: number;
  billingInterval: BillingInterval | null;
  /** First charge date when a trial applies, else null. */
  trialEndsOn: Date | null;
  /**
   * True while these amounts are Paddle's last confirmed ones but the buyer has since changed
   * the quantity, so a re-price is in flight. The UI mutes them rather than showing a guess.
   */
  repricing: boolean;
};

const BILLING_INTERVALS: BillingInterval[] = ['day', 'week', 'month', 'year'];

// Paddle types these as a string enum; narrow to the literal union the UI switches on.
function normalizePeriod(
  period: { frequency: number; interval: string } | undefined,
): TimePeriod | null {
  if (!period || !BILLING_INTERVALS.includes(period.interval as BillingInterval)) {
    return null;
  }
  return { frequency: period.frequency, interval: period.interval as BillingInterval };
}

function addTimePeriod(from: Date, period: TimePeriod): Date {
  const date = new Date(from.getTime());
  const { frequency, interval } = period;
  if (interval === 'day') {
    date.setDate(date.getDate() + frequency);
  } else if (interval === 'week') {
    date.setDate(date.getDate() + frequency * 7);
  } else if (interval === 'month') {
    date.setMonth(date.getMonth() + frequency);
  } else {
    date.setFullYear(date.getFullYear() + frequency);
  }
  return date;
}

/**
 * Turns a Paddle `checkout.loaded` / `checkout.updated` payload into the numbers the order
 * summary shows: `recurring_totals` for the renewal price (during a trial the immediate `totals`
 * are all zero) and `totals` for the amount charged right now.
 */
export function summarizeCheckout(data: CheckoutEventsData, now: Date): CheckoutSummary | null {
  const item = data.items[0];
  if (!item) {
    return null;
  }
  const recurring = data.recurring_totals ?? data.totals;
  const quantity = Math.max(item.quantity, 1);
  const trialPeriod = normalizePeriod(item.trial_period);
  return {
    currencyCode: data.currency_code,
    quantity,
    unitPrice: recurring.subtotal / quantity,
    subtotal: recurring.subtotal,
    discount: recurring.discount,
    tax: recurring.tax,
    dueToday: data.totals.total,
    recurringTotal: recurring.total,
    billingInterval: normalizePeriod(item.billing_cycle)?.interval ?? null,
    trialEndsOn: trialPeriod ? addTimePeriod(now, trialPeriod) : null,
    repricing: false,
  };
}

/**
 * Formats a Paddle amount (major units) in the checkout's currency for the viewer's locale.
 * Whole amounts drop the cents ("$0", "$32/month") so the two numbers that matter read at a
 * glance; anything with tax on it keeps them ("$34.56/month").
 */
export function formatAmount(amount: number, currencyCode: string): string {
  const fractionDigits = Number.isInteger(amount) ? 0 : undefined;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

/** "$32.00/month": the recurring price with its cycle, compact enough not to wrap in the summary. */
export function formatRecurring(amount: number, summary: CheckoutSummary): string {
  const money = formatAmount(amount, summary.currencyCode);
  if (summary.billingInterval === 'year') {
    return `${money}/${getTranslation('year')}`;
  }
  if (summary.billingInterval === 'month') {
    return `${money}/${getTranslation('month')}`;
  }
  return money;
}

/**
 * Re-prices a summary for a quantity the buyer just picked, so the totals move the instant the
 * stepper is tapped instead of waiting a round trip for Paddle's `checkout.items.updated`.
 *
 * Undiscounted, every amount scales linearly with quantity (one price, one tax rate), so this is
 * exact. A discount breaks that: Paddle's checkout events carry only the code and id, never the
 * type, so there is no way to tell a percentage code (scales) from a flat one (does not). Rather
 * than guess low and promise a total we will not charge, the last confirmed amounts are kept and
 * flagged as `repricing` until Paddle answers with its own.
 */
export function withQuantity(summary: CheckoutSummary, quantity: number): CheckoutSummary {
  if (quantity === summary.quantity) {
    return summary;
  }
  if (summary.discount > 0) {
    return { ...summary, repricing: true };
  }
  const scale = quantity / summary.quantity;
  return {
    ...summary,
    quantity,
    subtotal: summary.subtotal * scale,
    tax: summary.tax * scale,
    dueToday: summary.dueToday * scale,
    recurringTotal: summary.recurringTotal * scale,
  };
}
